import { supabase } from './supabase';

// ============================================
// TYPES
// ============================================

export interface DashboardAccessCode {
  id: string;
  code: string;
  name: string;
  dashboard_id: string;
  created_at: string;
  created_by: string;
  last_used_at: string | null;
  use_count: number;
}

export interface GenerateCodeResult {
  success: boolean;
  code?: string;
  code_id?: string;
  dashboard_id?: string;
  dashboard_name?: string;
  name?: string;
  error?: string;
}

export interface ValidateCodeResult {
  success: boolean;
  dashboard_id?: string;
  household_id?: string;
  dashboard_name?: string;
  code_name?: string;
  error?: string;
}

// ============================================
// GENERATE ACCESS CODE (Admin only)
// ============================================

/**
 * Generate a permanent 6-digit access code for a dashboard
 * Only admins can generate codes
 */
export async function generateDashboardAccessCode(
  dashboardId: string,
  name?: string
): Promise<GenerateCodeResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('generate_dashboard_access_code', {
      p_dashboard_id: dashboardId,
      p_created_by: user.id,
      p_name: name || null,
    });

    if (error) {
      console.error('Failed to generate access code:', error);
      return { success: false, error: error.message };
    }

    return data as GenerateCodeResult;
  } catch (err) {
    console.error('Error generating access code:', err);
    return { success: false, error: 'Failed to generate code' };
  }
}

// ============================================
// VALIDATE ACCESS CODE (For kiosk devices)
// ============================================

/**
 * Validate a 6-digit access code (called by kiosk/tablet)
 * Returns dashboard info on success
 */
export async function validateDashboardAccessCode(
  code: string,
  deviceId?: string
): Promise<ValidateCodeResult> {
  try {
    const { data, error } = await supabase.rpc('validate_dashboard_access_code', {
      p_code: code,
      p_device_id: deviceId || getDeviceId(),
    });

    if (error) {
      console.error('Failed to validate access code:', error);
      return { success: false, error: error.message };
    }

    return data as ValidateCodeResult;
  } catch (err) {
    console.error('Error validating access code:', err);
    return { success: false, error: 'Failed to validate code' };
  }
}

// ============================================
// REVOKE ACCESS CODE (Admin only)
// ============================================

/**
 * Revoke an access code so it can no longer be used
 */
export async function revokeDashboardAccessCode(
  codeId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('revoke_dashboard_access_code', {
      p_code_id: codeId,
      p_revoked_by: user.id,
    });

    if (error) {
      console.error('Failed to revoke access code:', error);
      return { success: false, error: error.message };
    }

    return data as { success: boolean; error?: string };
  } catch (err) {
    console.error('Error revoking access code:', err);
    return { success: false, error: 'Failed to revoke code' };
  }
}

// ============================================
// LIST ACCESS CODES FOR DASHBOARD
// ============================================

/**
 * List all active access codes for a dashboard
 */
export async function listDashboardAccessCodes(
  dashboardId: string
): Promise<DashboardAccessCode[]> {
  try {
    const { data, error } = await supabase.rpc('list_dashboard_access_codes', {
      p_dashboard_id: dashboardId,
    });

    if (error) {
      console.error('Failed to list access codes:', error);
      return [];
    }

    return (data || []) as DashboardAccessCode[];
  } catch (err) {
    console.error('Error listing access codes:', err);
    return [];
  }
}

// ============================================
// DEVICE ID MANAGEMENT
// ============================================

const DEVICE_ID_KEY = 'foundry_hub_device_id';

/**
 * Get a unique device ID for this device
 * Uses localStorage to persist across sessions
 */
export function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

// ============================================
// KIOSK SESSION MANAGEMENT
// ============================================

const KIOSK_SESSION_KEY = 'foundry_hub_kiosk_session';

export interface KioskSession {
  dashboardId: string;
  householdId: string;
  dashboardName: string;
  codeName: string;
  validatedAt: string;
}

/**
 * Get the current kiosk session from localStorage
 */
export function getKioskSession(): KioskSession | null {
  const stored = localStorage.getItem(KIOSK_SESSION_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as KioskSession;
  } catch {
    return null;
  }
}

/**
 * Save a kiosk session to localStorage
 */
export function setKioskSession(session: KioskSession): void {
  localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
}

/**
 * Clear the kiosk session
 */
export function clearKioskSession(): void {
  localStorage.removeItem(KIOSK_SESSION_KEY);
}

/**
 * Check if currently in kiosk mode
 */
export function isKioskMode(): boolean {
  return getKioskSession() !== null;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format a 6-digit code with a space in the middle for readability
 * e.g., "123456" -> "123 456"
 */
export function formatCode(code: string): string {
  if (code.length !== 6) return code;
  return `${code.substring(0, 3)} ${code.substring(3)}`;
}

// ============================================
// KIOSK DATA FETCHING
// ============================================

export interface KioskAnnouncement {
  id: string;
  title: string;
  message: string | null;
  icon: string;
  priority: string;
  color: string | null;
  is_sticky: boolean;
  created_at: string;
  creator?: {
    name: string;
    nickname: string | null;
    avatar_emoji: string | null;
  };
}

export interface KioskWeather {
  current: {
    temp: number;
    feels_like: number;
    humidity: number;
    description: string;
    icon: string;
    wind_speed: number;
  };
  forecast: Array<{
    date: string;
    temp_high: number;
    temp_low: number;
    icon: string;
    description: string;
  }>;
  location: string;
  cached: boolean;
}

export interface KioskInspiration {
  quote_text: string;
  author: string | null;
  source_name: string;
  background_color: string | null;
  background_image_url: string | null;
}

export interface KioskDataResponse {
  announcements?: KioskAnnouncement[];
  weather?: KioskWeather;
  inspiration?: KioskInspiration;
  error?: string;
}

export interface KioskDataOptions {
  include?: {
    announcements?: boolean;
    weather?: boolean;
    inspiration?: boolean;
  };
  weatherOptions?: {
    lat: number;
    lon: number;
    units?: 'imperial' | 'metric';
  };
}

/**
 * Fetch dashboard data for kiosk mode
 * Uses Edge Function to bypass RLS since kiosk has no authenticated user
 */
export async function getKioskData(
  householdId: string,
  options: KioskDataOptions = {}
): Promise<KioskDataResponse> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/get-kiosk-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        householdId,
        include: options.include,
        weatherOptions: options.weatherOptions,
      }),
    });

    if (!response.ok) {
      console.error('Kiosk data fetch error:', response.status, response.statusText);
      return { error: `HTTP ${response.status}` };
    }

    const data = await response.json();

    if (data?.error) {
      console.error('Kiosk data error:', data.error);
      return { error: data.error };
    }

    return data as KioskDataResponse;
  } catch (error) {
    console.error('Failed to fetch kiosk data:', error);
    return { error: 'Failed to fetch data' };
  }
}
